// 音響演出。Web Audio API でその場合成 (素材ファイル不要)。
//
// 役割:
// - イベント受信時の "ピコン♪" (event)
// - 大台超え時のレベルアップ風シーケンス (levelup)
//
// AudioContext はブラウザの autoplay policy によりユーザ操作前に音が出ない。
// 初回 play 時に作る + ミュート解除クリック自体がユーザ操作になるので実用上問題ない。
// 全ての音は masterGain を経由するので、setVolume() でまとめて音量調整できる。

const MUTED_KEY = 'feel-so-good:sound:muted';
const VOLUME_KEY = 'feel-so-good:sound:volume';
const EVENT_CHIME_MIN_GAP_MS = 700;
const DEFAULT_VOLUME = 0.5;

export type SoundKind = 'event' | 'levelup';

interface AudioGraph {
  readonly ctx: AudioContext;
  readonly dest: GainNode;
}

export class SoundController {
  private graph: AudioGraph | null = null;
  private muted: boolean;
  private volume: number;
  private lastEventChimeAt = 0;

  constructor() {
    this.muted = window.localStorage.getItem(MUTED_KEY) === '1';
    this.volume = readStoredVolume();
  }

  isMuted(): boolean {
    return this.muted;
  }

  setMuted(value: boolean): void {
    this.muted = value;
    window.localStorage.setItem(MUTED_KEY, value ? '1' : '0');
    if (!value) this.ensureContext();
  }

  toggle(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  getVolume(): number {
    return this.volume;
  }

  /** 0..1 の範囲。範囲外は clamp。 */
  setVolume(v: number): void {
    const clamped = clamp01(v);
    this.volume = clamped;
    window.localStorage.setItem(VOLUME_KEY, String(clamped));
    if (this.graph !== null) {
      this.graph.dest.gain.value = clamped;
    }
  }

  play(kind: SoundKind): void {
    if (this.muted) return;
    const audio = this.ensureContext();
    if (audio === null) return;

    switch (kind) {
      case 'event': {
        const now = performance.now();
        if (now - this.lastEventChimeAt < EVENT_CHIME_MIN_GAP_MS) return;
        this.lastEventChimeAt = now;
        playEventChime(audio);
        return;
      }
      case 'levelup': {
        playLevelUp(audio);
        return;
      }
    }
  }

  private ensureContext(): AudioGraph | null {
    if (this.graph === null) {
      let ctx: AudioContext;
      try {
        ctx = new AudioContext();
      } catch {
        return null;
      }
      const dest = ctx.createGain();
      dest.gain.value = this.volume;
      dest.connect(ctx.destination);
      this.graph = { ctx, dest };
    }
    if (this.graph.ctx.state === 'suspended') {
      void this.graph.ctx.resume();
    }
    return this.graph;
  }
}

// 100 の倍数を跨いだら levelup を鳴らす。
const LEVELUP_INTERVAL = 100;

export function pickMilestoneSound(prevCount: number, nextCount: number): SoundKind | null {
  if (Math.floor(prevCount / LEVELUP_INTERVAL) < Math.floor(nextCount / LEVELUP_INTERVAL)) {
    return 'levelup';
  }
  return null;
}

// --- 内部: 個別の音色定義 -----------------------------------------------------

function clamp01(v: number): number {
  if (Number.isNaN(v)) return 0;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

function readStoredVolume(): number {
  const raw = window.localStorage.getItem(VOLUME_KEY);
  if (raw === null) return DEFAULT_VOLUME;
  const n = Number(raw);
  if (!Number.isFinite(n)) return DEFAULT_VOLUME;
  return clamp01(n);
}

function note(
  audio: AudioGraph,
  freq: number,
  startOffset: number,
  duration: number,
  gain: number,
  type: OscillatorType,
): void {
  const t = audio.ctx.currentTime + startOffset;
  const osc = audio.ctx.createOscillator();
  const env = audio.ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  env.gain.setValueAtTime(0, t);
  env.gain.linearRampToValueAtTime(gain, t + 0.01);
  env.gain.exponentialRampToValueAtTime(0.001, t + duration);
  osc.connect(env).connect(audio.dest);
  osc.start(t);
  osc.stop(t + duration + 0.05);
}

// 上昇アルペジオ C5 → E5 → G5 → C6
function playEventChime(audio: AudioGraph): void {
  note(audio, 523.25, 0.0, 0.18, 0.12, 'triangle');
  note(audio, 659.25, 0.07, 0.18, 0.12, 'triangle');
  note(audio, 783.99, 0.14, 0.18, 0.12, 'triangle');
  note(audio, 1046.5, 0.21, 0.3, 0.15, 'triangle');
}

// RPG 風駆け上がり + 着地
function playLevelUp(audio: AudioGraph): void {
  const seq: ReadonlyArray<number> = [523.25, 659.25, 783.99, 1046.5, 1318.51, 1568.0];
  seq.forEach((f, i) => note(audio, f, i * 0.08, 0.15, 0.13, 'triangle'));
  const landAt = seq.length * 0.08;
  note(audio, 2093.0, landAt, 0.5, 0.16, 'triangle');
  note(audio, 1568.0, landAt, 0.5, 0.11, 'sine');
}
